/**
 * useCountdown — tick a countdown to an absolute ISO/Date deadline.
 *
 * Returns the seconds remaining (rounded down to integer; 0 if expired
 * or null deadline). Refreshes every second via setInterval. Stops the
 * interval automatically when the deadline is null or already past, so
 * idle pages don't spin a 1Hz re-render loop.
 *
 * Usage:
 *   const seconds = useCountdown(player.disconnect_grace_until);
 *   const seconds = useCountdown(game.turn_expires_at);
 */
import { useEffect, useState } from "react";

function _parseDeadline(deadline) {
  if (!deadline) return null;
  if (deadline instanceof Date) return deadline.getTime();
  const ms = Date.parse(deadline);
  return Number.isFinite(ms) ? ms : null;
}

function _secondsLeft(deadlineMs) {
  if (deadlineMs == null) return 0;
  const diff = deadlineMs - Date.now();
  return diff > 0 ? Math.floor(diff / 1000) : 0;
}

export function useCountdown(deadline, onExpire) {
  const deadlineMs = _parseDeadline(deadline);
  const [seconds, setSeconds] = useState(() => _secondsLeft(deadlineMs));

  useEffect(() => {
    if (deadlineMs == null) {
      setSeconds(0);
      return undefined;
    }
    // Sync immediately on deadline change so the first frame after a
    // re-render reflects the new value (rather than waiting up to a
    // second for the interval to fire).
    const initial = _secondsLeft(deadlineMs);
    setSeconds(initial);
    if (Date.now() >= deadlineMs) {
      // Already expired by the time we mount this deadline. Fire the
      // callback once so the parent can refetch the auto-resolved
      // server state.
      if (typeof onExpire === "function") onExpire();
      return undefined;
    }

    let fired = false;
    const id = setInterval(() => {
      const remaining = _secondsLeft(deadlineMs);
      setSeconds(remaining);
      if (remaining <= 0) {
        clearInterval(id);
        if (!fired && typeof onExpire === "function") {
          fired = true;
          onExpire();
        }
      }
    }, 1000);
    return () => clearInterval(id);
    // onExpire is intentionally not in deps — callers should pass a
    // stable callback (useCallback or a ref); changing it mid-tick
    // would restart the interval.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadlineMs]);

  return seconds;
}
